import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "components/Card";
import InputField from "components/InputField";
import TextField from "components/TextField";
import Button from "components/Button";
import Slider from "components/Slider";
import {
  getTemplateAPI,
  getTemplateQuestionsAPI,
  updateTemplateAPI,
  createTemplateQuestionAPI,
  updateTemplateQuestionAPI,
  deleteTemplateQuestionAPI,
} from "api/apiHelper";
import { toast } from "react-toastify";
import { CreateQuestionData, Question } from "types/template-builder-types";
import { MdDelete, MdAdd } from "react-icons/md";

interface QuestionFormData {
  id?: number;
  question_text: string;
  weightage: number;
  options: {
    "1": string;
    "2": string;
    "3": string;
    "4": string;
    "5": string;
  };
}

const EditTemplate = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    is_active: true,
  });
  const [questions, setQuestions] = useState<QuestionFormData[]>([]);

  useEffect(() => {
    if (templateId) {
      fetchTemplateData();
    }
  }, [templateId]);

  const fetchTemplateData = async () => {
    try {
      setIsLoading(true);
      const templateResponse = await getTemplateAPI(Number(templateId));
      const questionsResponse = await getTemplateQuestionsAPI(Number(templateId));

      const template = templateResponse.data;
      setFormData({
        name: template.name,
        description: template.description,
        is_active: template.is_active,
      });

      const questionsData: QuestionFormData[] = questionsResponse.data.map(
        (q: Question) => ({
          id: q.id,
          question_text: q.question_text,
          weightage: parseFloat(q.weightage),
          options: q.options,
        })
      );

      setQuestions(questionsData.length > 0 ? questionsData : [
        {
          question_text: "",
          weightage: 0,
          options: {
            "1": "",
            "2": "",
            "3": "",
            "4": "",
            "5": "",
          },
        },
      ]);
    } catch (error: any) {
      toast.error("Failed to load template data");
      navigate("/admin-appraisals");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleQuestionChange = (
    index: number,
    field: keyof QuestionFormData,
    value: any
  ) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      [field]: value,
    };
    setQuestions(updatedQuestions);
  };

  const handleOptionChange = (
    questionIndex: number,
    optionKey: "1" | "2" | "3" | "4" | "5",
    value: string
  ) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      options: {
        ...updatedQuestions[questionIndex].options,
        [optionKey]: value,
      },
    };
    setQuestions(updatedQuestions);
  };

  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        question_text: "",
        weightage: 0,
        options: {
          "1": "",
          "2": "",
          "3": "",
          "4": "",
          "5": "",
        },
      },
    ]);
  };

  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const calculateTotalWeightage = () => {
    return questions.reduce((sum, q) => sum + (q.weightage || 0), 0);
  };

  const getRemainingWeightage = () => {
    return 100 - calculateTotalWeightage();
  };

  const validateQuestions = () => {
    for (const question of questions) {
      if (!question.question_text.trim()) {
        return "All questions must have text";
      }
      if (question.weightage <= 0) {
        return "All questions must have a weightage greater than 0";
      }
      for (let i = 1; i <= 5; i++) {
        if (!question.options[i.toString() as "1" | "2" | "3" | "4" | "5"].trim()) {
          return `All rating options (1-5) must be filled for each question`;
        }
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.description.trim()) {
      toast.error("Please fill in all required template fields");
      return;
    }

    const validationError = validateQuestions();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const totalWeightage = calculateTotalWeightage();
    if (totalWeightage !== 100) {
      toast.error(`Total weightage must be exactly 100%. Current: ${totalWeightage}%`);
      return;
    }

    setIsSubmitting(true);

    try {
      await updateTemplateAPI(Number(templateId!), formData);

      const existingQuestionsResponse = await getTemplateQuestionsAPI(Number(templateId!));
      const existingQuestionIds = new Set<number>(
        existingQuestionsResponse.data.map((q: Question) => q.id)
      );

      const formQuestionIds = new Set<number>(
        questions.filter((q) => q.id !== undefined).map((q) => q.id!)
      );

      const questionsToDelete = Array.from(existingQuestionIds).filter(
        (id: number) => !formQuestionIds.has(id)
      );
      for (const questionId of questionsToDelete) {
        await deleteTemplateQuestionAPI(questionId);
      }

      for (const question of questions) {
        if (question.id) {
          await updateTemplateQuestionAPI(question.id, {
            question_text: question.question_text,
            weightage: question.weightage,
            options: question.options,
          });
        } else {
          const questionData: CreateQuestionData = {
            template_id: Number(templateId!),
            question_text: question.question_text,
            weightage: question.weightage,
            options: question.options,
          };
          await createTemplateQuestionAPI(questionData);
        }
      }

      toast.success("Template updated successfully!");
      navigate("/admin-appraisals");
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to update template";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalWeightage = calculateTotalWeightage();
  const remainingWeightage = getRemainingWeightage();

  if (isLoading) {
    return (
      <div className="mt-5 flex items-center justify-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Loading template...</p>
      </div>
    );
  }

  return (
    <div className="mt-5">
      <Card extra="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
            Edit Template
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Update the template details and questions below.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white border-b pb-2">
              Template Information
            </h3>

            <InputField
              id="name"
              name="name"
              label="Template Name *"
              type="text"
              placeholder="Enter template name"
              extra="w-full"
              variant="outlined"
              value={formData.name}
              onChange={handleChange}
              required
            />

            <TextField
              id="description"
              label="Description *"
              placeholder="Enter template description"
              extra="w-full"
              rows={4}
              value={formData.description}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  description: e.target.value,
                });
              }}
            />

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-brand-500 focus:ring-brand-500 border-gray-300 rounded"
              />
              <label
                htmlFor="is_active"
                className="ml-2 text-sm font-medium text-navy-700 dark:text-white"
              >
                Active
              </label>
            </div>
          </div>

          <div className="space-y-4 pt-6 border-t">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                Questions
              </h3>
              <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-end gap-4">
                <div className="text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Total: </span>
                  <span
                    className={`font-semibold ${
                      totalWeightage === 100
                        ? "text-green-600 dark:text-green-400"
                        : totalWeightage > 100
                        ? "text-red-600 dark:text-red-400"
                        : "text-blue-600 dark:text-blue-400"
                    }`}
                  >
                    {totalWeightage}%
                  </span>
                  <span className="text-gray-600 dark:text-gray-400 ml-2">
                    Remaining: {remainingWeightage}%
                  </span>
                </div>
                <Button
                  type="button"
                  varient="secondary"
                  onClick={addQuestion}
                  className="flex items-center gap-2 w-full sm:w-auto"
                >
                  <MdAdd className="h-4 w-4" />
                  Add Question
                </Button>
              </div>
            </div>

            {questions.map((question, index) => (
              <div
                key={question.id || index}
                className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-4 bg-gray-50 dark:bg-navy-700/30"
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-md font-semibold text-navy-700 dark:text-white">
                    Question # {index + 1}
                  </h4>
                  {questions.length > 1 && (
                    <Button
                      type="button"
                      varient="danger"
                      onClick={() => removeQuestion(index)}
                      className="flex items-center gap-1 px-3 py-1"
                    >
                      <MdDelete className="h-4 w-4" />
                      Remove
                    </Button>
                  )}
                </div>

                <TextField
                  id={`question_text_${index}`}
                  label="Question Text *"
                  placeholder="Enter the question"
                  extra="w-full [&_textarea]:bg-white dark:[&_textarea]:bg-navy-800"
                  rows={2}
                  value={question.question_text}
                  onChange={(e) =>
                    handleQuestionChange(index, "question_text", e.target.value)
                  }
                />

                <Slider
                  id={`weightage_${index}`}
                  label="Weightage (%) *"
                  value={question.weightage || 0}
                  min={0}
                  max={100}
                  step={5}
                  onChange={(value) => {
                    handleQuestionChange(index, "weightage", value);
                  }}
                  extra="w-full"
                  showError={question.weightage > remainingWeightage + question.weightage}
                />

                <div className="space-y-2">
                  <label className="text-sm font-bold text-navy-700 dark:text-white">
                    Rating Options (1-5) *
                  </label>
                  {(["1", "2", "3", "4", "5"] as const).map((key) => (
                    <InputField
                      key={key}
                      id={`option_${index}_${key}`}
                      label={`Option ${key} *`}
                      type="text"
                      placeholder={`Enter text for rating ${key}`}
                      extra="w-full [&_input]:bg-white dark:[&_input]:bg-navy-800"
                      variant="outlined"
                      value={question.options[key]}
                      onChange={(e) =>
                        handleOptionChange(index, key, e.target.value)
                      }
                      required
                    />
                  ))}
                </div>
              </div>
            ))}

            {questions.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p>No questions added yet. Click "Add Question" to get started.</p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              varient="secondary"
              onClick={() => navigate("/admin-appraisals")}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" varient="primary" disabled={isSubmitting}>
              {isSubmitting ? "Updating..." : "Update Template"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default EditTemplate;
